import type { Neighborhood } from "@mm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { day } from "@/lib/dates";
import { Badge, Button, Card, ConfirmButton, Empty, ErrorText, Field, Input, Section, Select } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

interface Code { id: string; code: string; uses: number; maxUses: number | null; expiresAt: string | null; disabledAt: string | null }
interface Cohort { id: string; name: string; season: string; startDate: string | null; neighborhoodName: string; members: number; codes: Code[] }

function CopyCode({ code }: { code: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(code);
        } catch {
          /* clipboard unavailable; code is selectable */
        }
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      className="select-all rounded-xl bg-butter px-3 py-2 font-mono text-lg font-bold tracking-widest"
      aria-label={`Copy code ${code}`}
    >
      {code} <span className="ml-1 text-xs font-sans tracking-normal">{done ? "Copied" : "Copy"}</span>
    </button>
  );
}

export default function Cohorts() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["cohorts"], queryFn: () => api<Cohort[]>("/v1/admin/cohorts") });
  const hoods = useQuery({ queryKey: ["hoods"], queryFn: () => api<Neighborhood[]>("/v1/neighborhoods"), staleTime: 86_400_000 });
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ name: "", neighborhoodId: "", season: "", startDate: "" });
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["cohorts"] });

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader title="Cohorts & codes" back="/more" action={!creating ? <Button onClick={() => setCreating(true)} className="!px-4">New cohort</Button> : null} />
      {creating ? (
        <Card className="mb-4">
          <p className="mb-3 text-sm text-muted">Creates the cohort's Village chat (you're added automatically) and a code moms enter at sign-up.</p>
          <Field label="Name">{(i) => <Input id={i} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Lincoln Park · Spring '26" />}</Field>
          <Field label="Neighborhood">
            {(i) => (
              <Select id={i} value={f.neighborhoodId} onChange={(e) => setF({ ...f, neighborhoodId: e.target.value })}>
                <option value="">Choose…</option>
                {(hoods.data ?? []).map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </Select>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Season">{(i) => <Input id={i} value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })} placeholder="Spring 2026" />}</Field>
            <Field label="Start date">{(i) => <Input id={i} type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} />}</Field>
          </div>
          <ErrorText error={error} />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              loading={busy === "create"}
              disabled={!f.name || !f.neighborhoodId || !f.season}
              onClick={() => run("create", async () => {
                await api("/v1/admin/cohorts", { method: "POST", body: { name: f.name, neighborhoodId: Number(f.neighborhoodId), season: f.season, startDate: f.startDate || null } });
                setCreating(false);
                setF({ name: "", neighborhoodId: "", season: "", startDate: "" });
              })}
            >
              Create
            </Button>
          </div>
        </Card>
      ) : null}
      {!creating ? <ErrorText error={error ?? q.error} /> : null}

      <div className="grid gap-2">
        {(q.data ?? []).map((c) => (
          <Card key={c.id}>
            <button className="flex w-full items-center justify-between text-left" onClick={() => setOpen(open === c.id ? null : c.id)} aria-expanded={open === c.id}>
              <span>
                <span className="block font-semibold">{c.name}</span>
                <span className="block text-sm text-muted">{c.neighborhoodName} · {c.members} moms{c.startDate ? ` · started ${day(c.startDate)}` : ""}</span>
              </span>
              <span className="text-muted">{open === c.id ? "▲" : "▼"}</span>
            </button>
            {open === c.id ? <CohortCodes c={c} busy={busy} run={run} /> : null}
          </Card>
        ))}
      </div>
      {q.isSuccess && !q.data.length && !creating ? <Empty title="No cohorts yet">Create one, then share its code with the moms in that group.</Empty> : null}
    </>
  );
}

function CohortCodes({ c, busy, run }: { c: Cohort; busy: string | null; run(key: string, fn: () => Promise<unknown>): Promise<void> }) {
  const [custom, setCustom] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const members = useQuery({ queryKey: ["cohort-members", c.id], queryFn: () => api<{ id: string; firstName: string; lastName: string; email: string }[]>(`/v1/admin/cohorts/${c.id}/members`) });
  return (
    <div className="mt-3 border-t border-line pt-3">
      <Section title="Codes">
        <div className="grid gap-2">
          {c.codes.map((k) => (
            <div key={k.id} className="flex flex-wrap items-center gap-2">
              <CopyCode code={k.code} />
              <span className="text-sm text-muted">{k.uses}{k.maxUses ? ` / ${k.maxUses}` : ""} used{k.expiresAt ? ` · expires ${day(k.expiresAt)}` : ""}</span>
              {k.disabledAt ? <Badge tone="blush">disabled</Badge> : <ConfirmButton variant="ghost" label="Disable" confirmLabel="Disable code" onConfirm={() => run(`d${k.id}`, () => api(`/v1/admin/cohort-codes/${k.id}/disable`, { method: "POST" }))} />}
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-[1fr_6rem] gap-2">
          <Input placeholder="Custom code (optional)" value={custom} onChange={(e) => setCustom(e.target.value.toUpperCase())} aria-label="Custom code" />
          <Input placeholder="Max uses" inputMode="numeric" value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/\D/g, ""))} aria-label="Max uses" />
        </div>
        <Button variant="secondary" className="mt-2 w-full" loading={busy === `c${c.id}`} onClick={() => run(`c${c.id}`, async () => {
          await api(`/v1/admin/cohorts/${c.id}/codes`, { method: "POST", body: { ...(custom ? { code: custom } : {}), maxUses: maxUses ? Number(maxUses) : null } });
          setCustom("");
          setMaxUses("");
        })}>Add a code</Button>
      </Section>
      <Section title={`Roster (${members.data?.length ?? 0})`}>
        <ul className="divide-y divide-line">
          {(members.data ?? []).map((m) => <li key={m.id} className="py-2 text-sm"><b>{m.firstName} {m.lastName}</b> <span className="text-muted">· {m.email}</span></li>)}
        </ul>
      </Section>
    </div>
  );
}
